import type { InitialSyncResult } from './types';
import { bootstrap } from './service/Bootstrap';
import { runInitialSyncPipeline } from './service/InitialSync';
import { ServiceRunner } from './service/ServiceLoops';
import type { BootstrapResult } from './service/Bootstrap';

async function cleanupRuntime(
  runner: ServiceRunner | undefined,
  runtime?: BootstrapResult,
): Promise<void> {
  if (runner) {
    await runner.stop();
    return;
  }
  if (runtime) {
    await runtime.hasher.close();
    runtime.clients.close();
    runtime.db.close();
  }
}

function awaitShutdownSignals(): Promise<NodeJS.Signals> {
  return new Promise((resolve) => {
    const signals: NodeJS.Signals[] = ['SIGINT', 'SIGTERM'];
    const handler = (signal: NodeJS.Signals) => {
      for (const sig of signals) {
        process.off(sig, handler);
      }
      resolve(signal);
    };
    for (const sig of signals) {
      process.on(sig, handler);
    }
  });
}

export async function runInitialSync(params: {
  token?: string;
  rootPath?: string;
}): Promise<InitialSyncResult> {
  const runtime = await bootstrap(params);
  try {
    const result = await runInitialSyncPipeline(runtime, {
      blockUntilReady: true,
    });
    runtime.logger.info(
      {
        snapshotHash: result.snapshotHash,
        filesCount: result.filesCount,
        totalSize: result.totalSize,
      },
      'Initial sync completed',
    );
    return result;
  } finally {
    await runtime.hasher.close();
    runtime.clients.close();
    runtime.db.close();
  }
}

export async function runService(params: {
  token?: string;
  rootPath?: string;
}): Promise<void> {
  const runtime = await bootstrap(params);
  let runner: ServiceRunner | undefined;
  try {
    runner = new ServiceRunner(runtime);
    await runner.prepareWatcher(true);
    let initialCreatedAt: number | undefined;
    try {
      const initial = await runInitialSyncPipeline(runtime, {
        blockUntilReady: false,
      });
      runtime.logger.info(
        {
          snapshotHash: initial.snapshotHash,
          filesCount: initial.filesCount,
        },
        'Initial sync completed; entering continuous mode',
      );
      initialCreatedAt = initial.createdAt;
    } catch (error: any) {
      runtime.logger.warn(
        { err: error },
        'Initial sync failed; enqueuing snapshot job and continuing',
      );
      runtime.outbox.enqueueSnapshot(runtime.config.rootId, 0);
    }

    if (initialCreatedAt) {
      runner.recordInitialSnapshot(initialCreatedAt);
    }
    await runner.startLoops();
    await runner.enableWatcherProcessing();
    runtime.logger.info('Coderule scanner service is running');

    const signal = await awaitShutdownSignals();
    runtime.logger.info({ signal }, 'Shutdown signal received');
  } finally {
    await cleanupRuntime(runner, runtime);
  }
}

export type { InitialSyncResult } from './types';
